import TitleGenerator from "./TitleGenerator";
import Config from "./Config";
import Analytics from "./Analytics";
import { constructManifest } from "./MinHls";
import HlsJS from "hls.js";

// How long we expect requests to maximum take before sending an analytics event
const expectedAnalyticsTimings = {
    GET: 250,
    PUT: 400,
    POST: 400,
    DELETE: 400,
};

//Custom prototype functions
String.prototype.capitalizeFirstLetter = function() {
    return this.charAt(0).toUpperCase() + this.slice(1);
};

// Add string format function, "String {0} function {1} > {2}".format("format", 10, 5)
String.prototype.format = function () {
    let args = arguments;
    return this.replace(/{(\d+)}/g, (match, number) => (
        typeof args[number] !== undefined ? args[number] : match
    ));
};

class pLoader extends HlsJS.DefaultConfig.loader {
    constructor(config) {
        super(config);
        const load = this.load.bind(this);
        this.load = function(context, config, callbacks) {

            // Warning: dynamic playlists currently not supported!
            if (Config.hlsMinEnabled && !context.url.includes('dynamic.m3u8')) {
                if (!context.url.endsWith('?min=')) {
                    context.url += '?min=';
                }

                const onSuccess = callbacks.onSuccess;
                callbacks.onSuccess = function (response, stats, context) {
                    response.data = constructManifest(response.data, context.url);
                    onSuccess(response, stats, context);
                }
            }

            load(context, config, callbacks);
        };
    }
}

export default class Util {

    static fetch (url, successCallback, errorCallback, fetchInit) {

        fetchInit = Util.addRequestHeaders(fetchInit);

        const requestStartTimestamp = performance.now();

        fetch(url, fetchInit).then(function (response) {
            if (!response.ok) {
                throw new Error("Status " + response.statusText);
            }
            response.text().then((text) => {
                let data;
                try {
                    data = JSON.parse(text);
                } catch(e) {
                    data = text;
                }

                const requestTime = performance.now() - requestStartTimestamp;
                const expectedMax = expectedAnalyticsTimings[fetchInit.method];
                if (requestTime > expectedMax) {
                    Analytics.event("api_slow_request", {
                        url: fetchInit.method + " " + url.origin + url.pathname,
                        duration: requestTime / 1000,
                    });
                }

                successCallback(data, response.headers);
            });
        }).catch((error) => {
            Analytics.event("api_error", {
                url: fetchInit.method + " " + url.origin + url.pathname,
                details: error,
            });
            if (errorCallback) errorCallback(error);
        });
    }

    static addRequestHeaders (fetchInit) {
        fetchInit = fetchInit || {};
        if (Config.networkConfiguration === "public") {
            return fetchInit;
        }

        if ('headers' in fetchInit) {
            fetchInit.headers['X-Forzify-Client'] = Config.networkConfiguration;
        } else {
            fetchInit.headers = {'X-Forzify-Client': Config.networkConfiguration};
        }
        return fetchInit;
    }

    static xhrAddRequestHeaders (xhr) {
        if (Config.networkConfiguration === "public") {
            return xhr;
        }

        xhr.setRequestHeader('X-Forzify-Client', Config.networkConfiguration);
        return xhr;
    }

    static getHlsJsConfig () {
        // We need to distinguish between manifests, going to forzify server, and video segments
        // Headers must be sent to forzify server to generate the correct urls
        let isManifestUrl = (url) => (
            url.split("?")[0].endsWith(".m3u8")
        );
        return {
            pLoader: pLoader,
            fetchSetup: function (context, initParams) {
                if (isManifestUrl(context.url)) {
                    initParams = Util.addRequestHeaders(initParams)
                }
                return new Request(context.url, initParams)
            },
            xhrSetup: function (xhr, url) {
                if (isManifestUrl(url)) {
                    Util.xhrAddRequestHeaders(xhr);
                }
                return xhr;
            },
            liveSyncDurationCount: Config.allowPublicTagging ? 5 : 3,
            seekHoleNudgeDuration: 0.15,
            autoLevelEnabled: false,
        };
    }

    static padLeadingZeroes (num, places) {
        let s = '' + num;
        while (s.length < places) {
            s = '0' + s;
        }
        return s;
    }

    static getThumbnailUrl (videoAssetName, timestamp, hd=true) {
        let baseUrl = videoAssetName;
        let extension = '.jpg';

        if (!hd) {
            baseUrl = baseUrl.replace(/\/hd$/, '');
        }

        let leadingZeros = 1;
        if (baseUrl.indexOf('cloudfront.net') !== -1 || baseUrl.indexOf('amazonaws') !== -1) {
            leadingZeros = 5;
        }

        let seconds = Math.floor(timestamp / 1000) + 1;

        // Support setting full address to first available thumb
        let firstThumbSet = (/(.+\/(?:img_?\.)?)(\d+)\.jpg/g).exec(videoAssetName);
        if (firstThumbSet !== null) {
            baseUrl = firstThumbSet[1];
            leadingZeros = firstThumbSet[2].length;
            seconds += parseInt(firstThumbSet[2], 10);
            //TODO Hotfix for NTF which uses 0-indexed sequence
            seconds = Math.max(0, seconds-1);
        } else {
            baseUrl += '/';
        }

        return baseUrl + Util.padLeadingZeroes(seconds, leadingZeros) + extension;
    }

    static playlistShouldBePrivate (eventType, rating) {
        switch (eventType) {
            case "start phase":
            case "end phase":
            case "end of game":
            case "substitution":
            case "corner":
            case "medical treatment":
            case "throw-in":
            case "injury":
                return true;
            default:
                return rating < 2;
        }
    }

    static createEventRating (def, max, min) {
        return {
            min: min || def,
            default: def,
            max: max || def,
        }
    }

    static tagHasProp (tag, prop, value) {
        if (tag[prop] === undefined || tag[prop].value === undefined) return false;
        if (value === undefined) return true;
        return tag[prop].value === value;
    }

    static getEventRatingConfig (tag) {
        /* Returns {min: 1-5, default: 1-5, max: 1-5} */
        switch (tag.action) {

            // Never visible events
            case "medical treatment":
            case "free kick":
            case "throw-in":
            case "injury":
                return this.createEventRating(1);

            // Text-only events
            case "start phase":
            case "end phase":
            case "end of game":
            case "substitution":
            case "corner":
                return this.createEventRating(2);

            // Dynamic events
            case "offside":
                return this.createEventRating(2, 4);
            case "misc":
            case "yellow card":
                return this.createEventRating(3, 4);
            case "goal":
            case "red card":
            case "penalty":
                return this.createEventRating(4, 5);

            // More complex
            case "shot": {
                // Penalty miss?
                if (this.tagHasProp(tag, "shot type", "penalty")) {
                    return this.createEventRating(4, 5);
                }
                return this.createEventRating(2, 4);
            }

            default:
                // Shouldn't be reachable
                return this.createEventRating(1);
        }
    }

    static createAutomaticTitle (tag, game, score) {

        let generator = new TitleGenerator(tag, game, score);

        //TODO go through some missing translations after confirming tag schema
        try {
            switch (tag.action) {
                case "goal":
                    return generator.goal();
                case "yellow card":
                    return generator.yellowCard();
                case "red card":
                    return generator.redCard();
                case "penalty":
                    return generator.penalty();
                case "start phase":
                    return generator.startPhase();
                case "end phase":
                    return generator.endPhase();
                case "end of game":
                    return generator.endOfGame();
                case "shot":
                    return generator.shot();
                case "save":
                    return generator.save();
                case "injury":
                    return generator.injury();
                case "misc":
                    return generator.misc();
                case "free kick":
                    return generator.freekick();
                case "medical treatment":
                    return generator.medicalTreatment();
                case "throw-in":
                    return generator.throwIn();
                case "corner":
                    return generator.corner();
                case "substitution":
                    return generator.substitution();
                case "offside":
                    return generator.offside();
                default:
                    return generator.unknown();
            }
        } catch (e) {
            return generator.unknown();
        }
    }
}



// WEBPACK FOOTER //
// ./src/js/include/Util.js